import { Box, BoxProps } from '@mui/material';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { DetailedHTMLProps, ImgHTMLAttributes } from 'react';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { UploadFile } from '@/types';

const Player = dynamic(() => import('@mux/mux-player-react'), {
  ssr: false,
  loading: () => <div>Loading video player...</div>,
});

export interface StrapiImageProps {
  size: ImageSize;
  image: UploadFile;
  fixAxis?: FixAxis;
  className?: string;
  imageWidth?: number;
  imageHeight?: number;
}

type ImageSize = 'large' | 'small' | 'medium' | 'thumbnail' | 'logo' | 'url';
type FixAxis = 'width' | 'height' | 'none';
type NextImageType = Omit<
  DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>,
  'loading' | 'ref' | 'alt' | 'src' | 'srcSet'
> &
  Pick<BoxProps, 'sx'>;

export const StrapiImage = (props: StrapiImageProps & NextImageType) => {
  const { fixAxis: fixAxisProp, sx: sxProp, ...rest } = props;

  if (!props.image) {
    return <></>;
  }

  const {
    size,
    imageWidth,
    imageHeight,
    image: { formats, alternativeText, mime, width, height },
  } = props;

  const finalWidth = imageWidth ?? (formats && formats[size]?.width) ?? 400;
  const finalHeight = imageHeight ?? (formats && formats[size]?.height) ?? 300;

  const isVideo = mime.startsWith('video/');
  // Mux Player doesn't support WebM format, so use regular HTML5 video element
  const isWebM = mime.includes('webm');

  let fixAxis: {
    width?: string | number;
    height?: string | number;
    maxWidth?: string;
  };

  if (imageWidth || imageHeight) {
    fixAxis = {
      width: imageWidth ? `${imageWidth}px` : 'auto',
      height: imageHeight ? `${imageHeight}px` : 'auto',
      maxWidth: '100%',
    };
  } else {
    fixAxis = {
      width: `${width}px`,
      maxWidth: '100%',
      height: 'auto',
    };
  }

  // fix width by default
  if (fixAxisProp === 'height') {
    const heightValue = Number(imageHeight ?? height);
    fixAxis = {
      width: 'auto',
      height: isVideo ? heightValue - 16 : heightValue,
    };
  }

  if (fixAxisProp === 'none') {
    fixAxis = {};
  }

  if (isVideo) {
    // Use regular HTML5 video element for WebM files (Mux Player doesn't support WebM)
    if (isWebM) {
      return (
        <Box
          {...rest}
          className={`player-box ${props.className || ''}`}
          data-component="StrapiImage-Video-WebM"
          sx={{
            borderRadius: '1.25rem',
            border: 'none',
            overflow: 'hidden',
            display: 'flex',
            width: props.width + 'px' || width + 'px',
          }}
        >
          <video
            style={{
              ...fixAxis,
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
            src={getImageUrl({ image: props.image, size: props.size }) || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        </Box>
      );
    }

    // Use Mux Player for other video formats (MP4, HLS, DASH, etc.)
    return (
      <Box
        {...rest}
        className={`player-box ${props.className || ''}`}
        data-component="StrapiImage-Video"
        sx={{
          borderRadius: '1.25rem',
          border: 'none',
          overflow: 'hidden',
          display: 'flex',
          width: props.width + 'px' || width + 'px',
        }}
      >
        <Player
          style={{
            ...fixAxis,
            display: 'grid',
            // @ts-expect-error browser specific properties
            '--controls': 'none',
            '--media-object-fit': 'cover',
            '--media-object-position': 'center',
          }}
          src={getImageUrl({ image: props.image, size: props.size }) ?? ''}
          primaryColor="#1976d2"
          autoPlay={true}
          muted={true}
          loop={true}
          crossOrigin="crossOrigin"
          prefer-playback="mse"
          preload="auto"
        />
      </Box>
    );
  }

  return (
    <Box
      {...rest}
      className={`strapi-image-box ${props.className || ''}`}
      data-component="StrapiImage"
      sx={[
        {
          border: 'none !important',
          borderRadius: '0 !important',
          overflow: 'visible !important',
          img: fixAxis,
        },
        ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
      ]}
    >
      <Image
        src={getImageUrl({ image: props.image, size: props.size })!}
        width={finalWidth}
        height={finalHeight}
        alt={alternativeText || 'Image'}
        unoptimized
      />
    </Box>
  );
};

/** Use getImageUrl so /uploads/ is stripped for media at bucket root. Kept for any external callers. */
export const ImageUrl = (props: StrapiImageProps) =>
  getImageUrl({ image: props.image, size: props.size });
